import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Stack,
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Switch,
  Chip,
  Tooltip,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import api from '../../services/api';

const ManageAccounts = () => {
  const navigate = useNavigate();
  const [subusers, setSubusers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Decode JWT token and set current user
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setCurrentUser({
          id: decodedToken.sub,
          isAdmin: decodedToken.IsAdmin === "true", // Convert string to boolean
          role: decodedToken.role
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        setError('Failed to authenticate user');
      }
    }
  }, []);

  // Fetch users only if current user is admin
  useEffect(() => {
    if (!currentUser?.isAdmin) {
      setLoading(false);
      return;
    }

    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/getusers');
        setSubusers(response.data);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.response?.data?.message || 'Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentUser]);

  const handleToggleStatus = async (userId, currentStatus) => {
      setProcessingId(userId);
      try {
        const newStatus = !currentStatus;
        await api.patch(`/auth/users/${userId}/status`, { 
          deactivated: newStatus 
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        setSubusers(prev => prev.map(user => 
          user.id === userId ? { ...user, deactivated: newStatus } : user
        ));
      } catch (err) {
        console.error('Error updating user status:', err);
        setError(err.response?.data?.message || 'Failed to update user status');
        
        // Revert the UI state if the API call fails
        setSubusers(prev => prev.map(user => 
          user.id === userId ? { ...user, deactivated: currentStatus } : user
        ));
      } finally {
        setProcessingId(null);
      }
    };

  const canToggleUserStatus = (user) => {
    return currentUser?.isAdmin && 
           user.id !== currentUser?.id && 
           !user.isAdmin;
  };

  const handleNavigate = (path) => navigate(path);

  const getRegisterPath = () => {
    return currentUser?.role === 'POWERADMIN' ? '/accounts/register' : '/accounts/registeruser';
  };

  const getUserStatusChip = (deactivated) => (
    <Chip
      label={deactivated ? 'Inactive' : 'Active'}
      color={deactivated ? 'default' : 'success'}
      size="small"
      icon={deactivated ? <CancelIcon /> : <CheckCircleIcon />}
      sx={{ ml: 1 }}
    />
  );

  // Non-admin view
  if (currentUser && !currentUser.isAdmin) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          Account Management
        </Typography>
        
        <Card sx={{ mb: 4, boxShadow: 3 }}>
          <CardHeader title="Quick Actions" titleTypographyProps={{ variant: 'h6' }} />
          <Divider />
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<LockIcon />}
                onClick={() => handleNavigate('/accounts/changepassword')}
                sx={{ minWidth: 200 }}
              >
                Change Password
              </Button>
            </Box>
          </CardContent>
        </Card>
        <Alert severity="info" sx={{ mt: 2 }}>
          User management features are only available to administrators.
        </Alert>        
      </Box>
    );
  }

  // Admin view
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Account Management
      </Typography>

      <Card sx={{ mb: 4, boxShadow: 3 }}>
        <CardHeader title="Quick Actions" titleTypographyProps={{ variant: 'h6' }} />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<LockIcon />}
              onClick={() => handleNavigate('/accounts/changepassword')}
              sx={{ minWidth: 200 }}
            >
              Change Password
            </Button>
            
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => handleNavigate(getRegisterPath())}
              sx={{ minWidth: 200 }}
            >
              {currentUser?.role === 'POWERADMIN' ? 'Register Admin' : 'Add User'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 3 }}>
        <CardHeader 
          title="User Management" 
          titleTypographyProps={{ variant: 'h6' }}
          subheader="Manage all system users"
          action={
            <Button direction="row" alignItems="center" spacing={1}>
                <PeopleIcon sx={{ color: 'inherit' }} />
                <Typography variant="body1">{subusers.length} user{subusers.length !== 1 ? 's' : ''} in system</Typography>
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <>
              <List>
                {subusers.map((user) => (
                  <ListItem 
                    key={user.id}
                    sx={{
                      bgcolor: 'background.paper',
                      mb: 1,
                      borderRadius: 1,
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Avatar 
                        sx={{ 
                          bgcolor: user.deactivated 
                            ? 'action.disabled' 
                            : user.isAdmin
                              ? 'primary.main' 
                              : 'success.main',
                          color: 'common.white'
                        }}
                      >
                        {user.userName?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" component="span">
                            {user.userName}
                          </Typography>
                          {getUserStatusChip(user.deactivated)}
                          {user.isAdmin && (
                            <Chip
                              label="ADMIN"
                              color="primary"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      }
                      
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {canToggleUserStatus(user) ? (
                        <Switch
                          checked={!user.deactivated}
                          onChange={() => handleToggleStatus(user.id, user.deactivated)}
                          disabled={processingId === user.id}
                          color="primary"
                          sx={{ mr: 2 }}
                        />
                      ) : (
                        <Tooltip 
                          title={
                            user.id === currentUser?.id 
                              ? "You cannot modify your own status" 
                              : user.isAdmin
                                ? "Cannot modify admin users"
                                : "Insufficient permissions"
                          }
                        >
                          <WarningIcon color="action" sx={{ mr: 2 }} />
                        </Tooltip>
                      )}
                      
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManageAccounts;